import { useState, useEffect } from 'react';
import { Form, InputNumber, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import CustomButton from 'base/ui/components/Button';
import { SearchForm, SearchItem } from 'pages/Components/SearchBox';

interface TareWeightModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { numberOfTare: number; tareWeight: number; tareDeduction: number }) => Promise<void>;
  loading?: boolean;
}

const TareWeightModal: React.FC<TareWeightModalProps> = ({
  open,
  onClose,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [tareDeduction, setTareDeduction] = useState<number>(0);

  // Tính toán trừ bì khi số bì hoặc khối lượng bì thay đổi
  const calculateTareDeduction = (numberOfTare: number, tareWeight: number) => {
    const deduction = (numberOfTare || 0) * (tareWeight || 0);
    setTareDeduction(deduction);
    form.setFieldsValue({ tareDeduction: deduction });
  };

  // Xử lý submit
  const handleSubmit = async (values: any) => {
    try {
      const { numberOfTare, tareWeight, tareDeduction } = values;
      // Nếu không có tareDeduction từ form, tính toán từ numberOfTare và tareWeight
      const deduction = tareDeduction !== undefined && tareDeduction !== null
        ? tareDeduction
        : (numberOfTare || 0) * (tareWeight || 0);
      await onSave({
        numberOfTare: numberOfTare || 0,
        tareWeight: tareWeight || 0,
        tareDeduction: deduction,
      });
    } catch (error) {
      console.error('Error saving tare weight:', error);
      message.error(t('message.error'));
    }
  };

  const handleClose = () => {
    form.resetFields();
    setTareDeduction(0);
    onClose();
  };

  // Reset form khi modal mở/đóng
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTareDeduction(0);
    }
  }, [open, form]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={<h2 className="uppercase text-center text-base font-semibold">GHI NHẬN TRỪ BÌ</h2>}
      width={600}
      className="[&_.ant-modal-header]:!border-b-0 [&_.ant-modal-title]:!text-center"
    >
      <SearchForm
        form={form}
        onFinish={handleSubmit}
        column="col-1"
      >
        {/* Số bì */}
        <SearchItem
          name="numberOfTare"
          label="Số bì"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <InputNumber
            min={0}
            precision={0}
            style={{ width: '100%' }}
            placeholder="Nhập số bì"
            addonAfter="Cái"
            controls={false}
            onChange={(value) => {
              const tareWeight = form.getFieldValue('tareWeight') || 0;
              calculateTareDeduction(value || 0, tareWeight);
            }}
          />
        </SearchItem>

        {/* Khối lượng bì */}
        <SearchItem
          name="tareWeight"
          label="Khối lượng bì"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <InputNumber
            min={0}
            // precision={2}
            style={{ width: '100%' }}
            placeholder="Nhập khối lượng bì"
            addonAfter="Kg"
            controls={false}
            onChange={(value) => {
              const numberOfTare = form.getFieldValue('numberOfTare') || 0;
              calculateTareDeduction(numberOfTare, value || 0);
            }}
          />
        </SearchItem>

        {/* Trừ bì (tính toán) */}
        <SearchItem
          name="tareDeduction"
          label="Trừ bì (số bì * khối lượng bì)"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <InputNumber
            min={0}
            // precision={2}
            style={{ width: '100%' }}
            placeholder="Tự động tính hoặc nhập thủ công"
            addonAfter="Kg"
            controls={false}
          />
        </SearchItem>

        {/* Buttons */}
        <div className="mt-4 flex justify-end gap-2">
          <CustomButton
            type="default"
            htmlType="button"
            loading={loading}
            onClick={handleClose}
          >
            Thoát
          </CustomButton>
          <CustomButton
            type="primary"
            htmlType="submit"
            loading={loading}
          >
            Lưu
          </CustomButton>
        </div>
      </SearchForm>
    </Modal>
  );
};

export default TareWeightModal;

